import React from 'react';

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './BotaoVoltar.css';

/**
 * Componente BotaoVoltar
 * 
 * Botão reutilizável para voltar à página anterior no histórico de navegação.
 * Utiliza o hook `useNavigate` da biblioteca 'react-router-dom'.
 * 
 * Propriedades (Props):
 * - texto (string): Texto exibido no botão (padrão: "Voltar").
 * - destino (string): Rota explícita para navegação caso queira definir um destino fixo (ex: "/" ou "/listagem-artigos").
 * - rota (string): Alias para `destino`.
 * - fallback (string): Rota alternativa caso não haja histórico anterior (ex: "/").
 * - aoClicar (function): Função executada ao clicar no botão antes da navegação.
 * - className (string): Classes CSS adicionais ou personalizadas (padrão: "btn btn-outline-secondary botao-voltar").
 * - tipo (string): Tipo do botão HTML (padrão: "button" para evitar submissão involuntária de formulários).
 * - mostrarIcone (boolean): Se true, exibe o ícone de seta voltando (padrão: true).
 * - icone (ReactNode): Ícone customizado para substituir a seta padrão.
 * - disabled (boolean): Desabilita o botão se verdadeiro.
 * - ariaLabel (string): Descrição acessível para leitores de tela.
 * - children (ReactNode): Conteúdo customizado interno (se informado, substitui o texto padrão).
 */
const BotaoVoltar = ({
    texto = 'Voltar',
    destino,
    rota,
    fallback,
    aoClicar,
    className = 'btn-menu-nav botao-voltar',
    tipo = 'button',
    mostrarIcone = true,
    icone,
    disabled = false,
    ariaLabel = 'Voltar para a página anterior',
    children,
    ...outrasProps
}) => {
    const navigate = useNavigate();

    const tratarClique = (evento) => {
        // Se foi passada uma função aoClicar personalizada, executa
        if (aoClicar) {
            aoClicar(evento);
        }

        // Se o evento foi cancelado pelo desenvolvedor (ex: e.preventDefault()), interrompe a navegação
        if (evento && evento.defaultPrevented) {
            return;
        }

        const rotaDestino = destino || rota;

        if (rotaDestino) {
            navigate(rotaDestino);
        } else if (fallback && window.history?.state?.idx === 0) {
            // Se o usuário acessou diretamente pela URL e não tem histórico anterior no app
            navigate(fallback);
        } else {
            // Navega para a página anterior do histórico
            navigate(-1);
        }
    };

    return (
        <button
            type={tipo}
            className={className}
            onClick={tratarClique}
            disabled={disabled}
            aria-label={ariaLabel}
            {...outrasProps}
        >
            {/* Renderização do ícone (padrão: Bootstrap Icons bi-arrow-left) */}
            {mostrarIcone && (
                icone || <ArrowLeft className="icone-nav-btn" aria-hidden="true" size={16}></ArrowLeft>
            )}

            {/* Conteúdo do botão: texto ou elementos filhos passados via children */}
            {children ? children : texto}
        </button>
    );
};

export default BotaoVoltar;

